import type { ContractPermissionsResponse } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContractPermissions } from './fetchContractPermissions';

/**
 * Creates query options for fetching contract field permissions.
 *
 * @param params - The query parameters.
 * @param params.contractId - The UUID of the contract to fetch permissions for.
 * @param params.options - Optional query options to override defaults.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractPermissionsQueryOptions = <
  TData = ContractPermissionsResponse,
>({
  contractId,
  options,
}: {
  contractId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<ContractPermissionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: contractId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractPermissions(contractId, { signal })
      : skipToken,
    queryKey: queryKeysContract.getPermissions(contractId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * Custom hook for fetching field-level edit permissions for a contract.
 * Use this to dynamically enable/disable form fields in the UI.
 *
 * @param contractId - The UUID of the contract to fetch permissions for.
 * @param options - Optional query options to override defaults.
 * @returns An object containing the editable/readonly field lists and related query properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_permissions_contracts__contract_id__permissions_get
 */
export const useQueryContractPermissions = (
  contractId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<
      ContractPermissionsResponse,
      Error,
      ContractPermissionsResponse
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...getContractPermissionsQueryOptions<ContractPermissionsResponse>({
      contractId,
      options,
    }),
  });
};
